import { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box,
  Grid,
  CircularProgress,
  Button,
  Typography,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import PostAddIcon from '@mui/icons-material/PostAdd';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import { useOktaAuth } from '@okta/okta-react';
import {
  getCustomRankings,
  createCustomRanking,
  deleteCustomRanking,
  updateCustomRanking,
} from '../providers/productFinderAPI';
import CustomRanking from '../models/iCustomRanking';
import { ICustomRankingCreate } from '../models/iCustomRankingDto';
import { toast } from 'react-toastify';
import { AddCustomRankingDialog } from '../components/AddCustomRankingDialog';
import { EditCustomRankingDialog } from '../components/EditCustomRankingDialog';
import { useLogger } from '../hooks/useLogger';

interface ICustomRankingRow {
  id: number;
  salesOrg: number;
  productYNumber: string;
  productType: string;
  rank: number;
  modifiedBy: string;
}

const CustomRankings = () => {
  const { authState } = useOktaAuth();
  const logger = useLogger('CustomRankings');
  const [customRankings, setCustomRankings] = useState<CustomRanking[]>([]);
  const [areCustomRankingsLoading, setAreCustomRankingsLoading] = useState(true);

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [rowToEdit, setRowToEdit] = useState<CustomRanking | null>(null);

  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [rowIdToDelete, setRowIdToDelete] = useState<number | null>(null);

  const handleEditClick = (row: CustomRanking) => {
    setRowToEdit(row);
    setIsEditDialogOpen(true);
  };

  const handleDeleteClick = (id: number) => {
    setRowIdToDelete(id);
    setIsDeleteConfirmOpen(true);
  };

  const handleCloseDeleteConfirm = () => {
    setRowIdToDelete(null);
    setIsDeleteConfirmOpen(false);
  };

  const columns: GridColDef[] = [
    { field: 'salesOrg', headerName: 'Sales Org', flex: 1 },
    { field: 'productYNumber', headerName: 'Product Y Number', flex: 1 },
    { field: 'productType', headerName: 'Product Type', flex: 1 },
    { field: 'rank', headerName: 'Rank', type: 'number', flex: 1, align: 'left', headerAlign: 'left' },
    { field: 'modifiedBy', headerName: 'Modified By', flex: 1 },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      disableColumnMenu: true,
      width: 100,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <Tooltip title="Edit Ranking">
            <IconButton onClick={() => handleEditClick(params.row as CustomRanking)} color="primary">
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Ranking">
            <IconButton onClick={() => handleDeleteClick(params.row.id)} color="error">
              <DeleteForeverIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const fetchCustomRankings = useCallback(async () => {
    setAreCustomRankingsLoading(true);
    const bearerToken = authState?.accessToken?.accessToken;
    if (!bearerToken) {
      setAreCustomRankingsLoading(false);
      return;
    }
    try {
      const rankings = await getCustomRankings(bearerToken);
      setCustomRankings(rankings);
    } catch (error) {
      toast.error('Error fetching custom rankings');
    } finally {
      setAreCustomRankingsLoading(false);
    }
  }, [authState]);

  useEffect(() => {
    fetchCustomRankings();
  }, [fetchCustomRankings]);

  const rows: ICustomRankingRow[] = useMemo(() => {
    return customRankings.map((cr) => ({
      id: cr.id,
      salesOrg: cr.salesOrg,
      productYNumber: cr.productYNumber,
      productType: cr.productType,
      rank: cr.rank,
      modifiedBy: cr.modifiedBy,
    }));
  }, [customRankings]);

  const handleCreate = async (newRanking: ICustomRankingCreate) => {
    const bearerToken = authState?.accessToken?.accessToken;
    if (!bearerToken) {
      toast.error('Authentication error. Please sign in again.');
      return false;
    }
    try {
      await createCustomRanking(bearerToken, newRanking);
      toast.success('New ranking saved successfully!');
      await fetchCustomRankings(); // Re-fetch data to update the grid
      return true;
    } catch (error) {
      toast.error('Failed to save ranking. Please try again.');
      return false;
    }
  };

  const handleUpdate = async (rankingToUpdate: CustomRanking) => {
    const bearerToken = authState?.accessToken?.accessToken;
    if (!bearerToken) {
      toast.error('Authentication error.');
      return false;
    }

    try {
      const updatePayload = {
        rank: rankingToUpdate.rank,
        modifiedBy: rankingToUpdate.modifiedBy,
        productType: rankingToUpdate.productType,
      };

      await updateCustomRanking(bearerToken, rankingToUpdate.id, updatePayload);

      toast.success('Ranking updated successfully!');
      await fetchCustomRankings();
      return true;
    } catch (error) {
      toast.error('Failed to update ranking. Please try again.');
      return false;
    }
  };

  const handleConfirmDelete = async () => {
    if (rowIdToDelete === null) return;

    const bearerToken = authState?.accessToken?.accessToken;
    if (!bearerToken) {
      toast.error('Authentication error. Please sign in again.');
      return;
    }

    setIsDeleting(true);
    try {
      await deleteCustomRanking(bearerToken, rowIdToDelete);
      toast.success('Ranking deleted successfully!');
      await fetchCustomRankings(); // Refresh the grid
    } catch (error) {
      logger.UI.error('Delete operation failed', error);
      toast.error('Failed to delete ranking. Please try again.');
    } finally {
      setIsDeleting(false);
      handleCloseDeleteConfirm();
    }
  };

  if (areCustomRankingsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Typography variant="h4" component="h1">
            Custom Rankings
          </Typography>
        </Grid>
        <Grid
          sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}
          size={{
            xs: 12,
            md: 6
          }}>
          <Button
            variant={'contained'}
            startIcon={<PostAddIcon />}
            onClick={() => setIsAddDialogOpen(true)}
            sx={{
              width: { xs: '100%', sm: 'auto' },
              bgcolor: '#03387e',
              color: '#fff',
              '&:hover': {
                bgcolor: '#022a65',
              },
            }}
          >
            Add Custom Ranking
          </Button>
        </Grid>
      </Grid>
      <Box sx={{ flexGrow: 1, width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          disableDensitySelector={true}
          getRowHeight={() => 'auto'}
          density={'compact'}
          sx={{
            height: '100vh',
            width: '100%',
            border: '1px solid #ccc',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f5f5f5',
            },
            '& .MuiDataGrid-cell': {
              display: 'flex',
              alignItems: 'center',
            },
          }}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 25,
              },
            },
          }}
          pageSizeOptions={[5, 10, 25, 50]}
        />
      </Box>
      <AddCustomRankingDialog open={isAddDialogOpen} onClose={() => setIsAddDialogOpen(false)} onSave={handleCreate} />
      <EditCustomRankingDialog
        open={isEditDialogOpen}
        ranking={rowToEdit}
        onClose={() => setIsEditDialogOpen(false)}
        onSave={handleUpdate}
      />
      <Dialog open={isDeleteConfirmOpen} onClose={handleCloseDeleteConfirm}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this custom ranking? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteConfirm} disabled={isDeleting}>
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" disabled={isDeleting}>
            {isDeleting ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomRankings;
